import * as React from 'react';
import { Mapping } from '../../api/Mapping';
import { getBillingInformation, getEvent, setBillingInformation } from '../../api/GraphProvider';
import { Abo } from '../../api/Abo';
import { IAbo } from '../../api/model/IAbo';
import { LinkCard } from './LinkCard';
import { Stack } from '@fluentui/react/lib/Stack';
import { getEventId, removeCategory, removeProperties, save, setCategory, setProperties } from '../../service/appointment.service';
import { showMessage } from '../../service/globalState.service';
const { useState, useRef, useEffect } = React;

interface AboListProps {
  abos: IAbo[];
}

export const AboList: React.FC<AboListProps> = ({ abos }) => {
  const [linkedAbo, setLinkedAbo] = useState<IAbo>(null);
  const [canLink, setCanLink] = useState(true);

  const _eventId = useRef<string>();
  const _appGuid = useRef<string>();
  const _app = useRef<Office.AppointmentCompose>();
  useEffect(() => {
    const { item } = Office.context.mailbox;
    _app.current = item;
    checkLinkedStatus();
    checkIfLinkable();
  }, [])

  const checkLinkedStatus = async () => {
    _eventId.current = await getEventId();
    _appGuid.current = await getBillingInformation(_eventId.current);
    if (!_appGuid.current) return;

    const mapping = await Mapping.get(_appGuid.current);
    const linkedAbo = abos.find(a => a.Id = mapping.aboGuid) || await Abo.get(mapping.aboGuid);

    if (linkedAbo) setLinkedAbo(linkedAbo);
  }

  const checkIfLinkable = () => {
    if (!_app.current) return;
    _app.current.recurrence.getAsync(r => {
      if (r.value) setCanLink(false);
    })

    _app.current.requiredAttendees.getAsync(r => {
      if (r.value.length > 1) setCanLink(false);
    });

    _app.current.optionalAttendees.getAsync(r => {
      if (r.value.length > 0) setCanLink(false);
    })
  }

  const linkAbo = async (abo: IAbo) => {
    if (!_app.current || !_eventId.current) return;

    if (_appGuid.current) {
      await Mapping.update({
        appGuid: _appGuid.current,
        aboGuid: abo.Id
      });
    } else {
      _appGuid.current = await Mapping.create({
        aboGuid: abo.Id
      });
      await setBillingInformation(_eventId.current, _appGuid.current);
    }

    await setCategory();
    await setProperties(abo.Id, abo.ProjectGuid, abo.TaskGuid, _appGuid.current);
    await getEvent(_eventId.current);
    await save();
    setLinkedAbo(abo);
  }

  const unlinkAbo = async () => {
    if (!_app.current || !_eventId.current) return;
    await setBillingInformation(_eventId.current, '');
    _appGuid.current = '';
    await removeCategory();
    await removeProperties();
    await save();
    setLinkedAbo(null);
  }

  const renderAbo = (abo: IAbo): JSX.Element => {
    const isLinked = linkedAbo && linkedAbo.Id === abo.Id;
    return <LinkCard
      key={abo.Id}
      task={abo}
      isLinked={isLinked}
      disabled={!canLink}
      onClick={async () => {
        try {
          if (isLinked) {
            await unlinkAbo();
            return;
          }

          await linkAbo(abo);
        } catch (e) {
          showMessage({
            type: "error",
            text: 'Error while linking/unlinking task: ' + JSON.stringify(e)
          })
        }
      }} />;
  };

  return (
    <Stack tokens={{ childrenGap: 10 }}>
      {abos.map(renderAbo)}
    </Stack>
  );
}